import { infiniteQueryOptions, queryOptions, useInfiniteQuery, useMutation, useQuery, useQueryClient, type InfiniteData, type QueryClient } from '@tanstack/react-query'
import { authKeys } from '@/features/auth/api'
import { api, unwrap } from '@/lib/api'
import type { DataEnvelope, Media, MediaBulkAction, MediaBulkResult, MediaFolder, MediaSummary, MediaUsage, Paginated } from '@/types/api'
import { isTerminalStatus } from './lib/display'
import type { MediaListParams } from './lib/search'

export const MEDIA_PAGE_SIZE = 48

export const mediaKeys = {
  all: ['media'] as const,
  lists: () => [...mediaKeys.all, 'list'] as const,
  list: (params: MediaListParams) => [...mediaKeys.lists(), params] as const,
  summary: () => [...mediaKeys.all, 'summary'] as const,
  details: () => [...mediaKeys.all, 'detail'] as const,
  detail: (id: string) => [...mediaKeys.details(), id] as const,
  usage: (id: string) => [...mediaKeys.detail(id), 'usage'] as const,
  folders: () => [...mediaKeys.all, 'folders'] as const,
}

/* ------------------------------------------------------------------ queries */

export function mediaInfiniteOptions(params: MediaListParams) {
  return infiniteQueryOptions({
    queryKey: mediaKeys.list(params),
    queryFn: ({ pageParam, signal }) => api.get<Paginated<Media>>('/media', { signal, query: { ...params, per_page: MEDIA_PAGE_SIZE, cursor: pageParam } }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.meta?.next_cursor ?? undefined,
  })
}

/** Library list; polls while any loaded item is still uploading/processing. */
export function useMediaList(params: MediaListParams, enabled = true) {
  return useInfiniteQuery({
    ...mediaInfiniteOptions(params),
    enabled,
    refetchInterval: (query) => (query.state.data?.pages.some((p) => p.data.some((m) => !isTerminalStatus(m.status))) ? 4_000 : false),
  })
}

export const mediaSummaryOptions = queryOptions({
  queryKey: mediaKeys.summary(),
  queryFn: ({ signal }) => unwrap(api.get<DataEnvelope<MediaSummary>>('/media/summary', { signal })),
})

export function useMediaSummary(enabled = true) {
  return useQuery({ ...mediaSummaryOptions, enabled })
}

export const mediaFoldersOptions = queryOptions({
  queryKey: mediaKeys.folders(),
  queryFn: async ({ signal }) => {
    const res = await api.get<DataEnvelope<MediaFolder[]> | Paginated<MediaFolder>>('/media-folders', { signal })
    return res.data
  },
})

export function useMediaFolders(enabled = true) {
  return useQuery({ ...mediaFoldersOptions, enabled })
}

export function mediaDetailOptions(id: string) {
  return queryOptions({
    queryKey: mediaKeys.detail(id),
    queryFn: ({ signal }) => unwrap(api.get<DataEnvelope<Media>>(`/media/${id}`, { signal })),
    refetchInterval: (query) => (query.state.data && !isTerminalStatus(query.state.data.status) ? 2_000 : false),
  })
}

export function useMediaDetail(id: string | undefined) {
  return useQuery({ ...mediaDetailOptions(id ?? ''), enabled: !!id })
}

export function useMediaUsage(id: string | undefined) {
  return useQuery({
    queryKey: mediaKeys.usage(id ?? ''),
    queryFn: ({ signal }) => unwrap(api.get<DataEnvelope<MediaUsage>>(`/media/${id}/usage`, { signal })),
    enabled: !!id,
  })
}

export function downloadUrl(id: string): string {
  // Same-origin API route; the server 302s to a short-lived signed URL (attachment).
  return `${import.meta.env.VITE_API_BASE_URL ?? '/api/v1'}/media/${id}/download`
}

/* ---------------------------------------------------------------- mutations */

/** Lists, counts and storage after anything that changes the library. */
export function invalidateMedia(qc: QueryClient, opts: { id?: string; folders?: boolean; usage?: boolean } = {}) {
  void qc.invalidateQueries({ queryKey: mediaKeys.lists() })
  void qc.invalidateQueries({ queryKey: mediaKeys.summary() })
  if (opts.folders !== false) void qc.invalidateQueries({ queryKey: mediaKeys.folders() })
  if (opts.id) void qc.invalidateQueries({ queryKey: mediaKeys.detail(opts.id) })
  if (opts.usage !== false) {
    void qc.invalidateQueries({ queryKey: ['dashboard'] })
    void qc.invalidateQueries({ queryKey: authKeys.me() })
    void qc.invalidateQueries({ queryKey: ['onboarding'] })
  }
}

/** Replace one media object everywhere it's cached (lists + detail). */
export function patchMediaCache(qc: QueryClient, media: Media) {
  qc.setQueriesData<InfiniteData<Paginated<Media>>>({ queryKey: mediaKeys.lists() }, (data) =>
    data?.pages ? { ...data, pages: data.pages.map((p) => ({ ...p, data: p.data.map((m) => (m.id === media.id ? { ...m, ...media } : m)) })) } : data,
  )
  qc.setQueryData<Media>(mediaKeys.detail(media.id), (prev) => (prev ? { ...prev, ...media, variants: media.variants ?? prev.variants, metadata: media.metadata ?? prev.metadata } : media))
}

function removeFromLists(qc: QueryClient, ids: ReadonlySet<string>) {
  qc.setQueriesData<InfiniteData<Paginated<Media>>>({ queryKey: mediaKeys.lists() }, (data) =>
    data?.pages ? { ...data, pages: data.pages.map((p) => ({ ...p, data: p.data.filter((m) => !ids.has(m.id)) })) } : data,
  )
}

export interface MediaPatch {
  name?: string
  folder_id?: string | null
  valid_from?: string | null
  valid_until?: string | null
}

export function useUpdateMedia(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: MediaPatch) => unwrap(api.patch<DataEnvelope<Media>>(`/media/${id}`, input)),
    onSuccess: (media, input) => {
      patchMediaCache(qc, media)
      invalidateMedia(qc, { folders: 'folder_id' in input, usage: false })
    },
  })
}

export function useSetMediaTags(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (tagIds: string[]) => {
      const res = await api.put<DataEnvelope<Media> | undefined>(`/media/${id}/tags`, { tag_ids: tagIds })
      return res && typeof res === 'object' && 'data' in res ? res.data : null
    },
    onSuccess: (media) => {
      if (media) patchMediaCache(qc, media)
      invalidateMedia(qc, { id, folders: false, usage: false })
    },
  })
}

export function useDeleteMedia() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/media/${id}`),
    onSuccess: (_d, id) => {
      removeFromLists(qc, new Set([id]))
      invalidateMedia(qc, { id })
    },
  })
}

export function useRestoreMedia() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post<unknown>(`/media/${id}/restore`),
    onSuccess: (_d, id) => {
      removeFromLists(qc, new Set([id]))
      invalidateMedia(qc, { id })
    },
  })
}

export interface BulkInput {
  action: MediaBulkAction
  media_ids: string[]
  folder_id?: string | null
  tag_ids?: string[]
}

export function useBulkMedia() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: BulkInput) => unwrap(api.post<DataEnvelope<MediaBulkResult>>('/media/bulk', input)),
    onSuccess: (result, input) => {
      // Items that leave the current view (delete/restore) disappear immediately.
      if (input.action === 'delete' || input.action === 'restore') removeFromLists(qc, new Set(result.succeeded))
      for (const id of result.succeeded) void qc.invalidateQueries({ queryKey: mediaKeys.detail(id) })
      invalidateMedia(qc, { usage: input.action === 'delete' || input.action === 'restore' })
    },
  })
}

/* ------------------------------------------------------------------ folders */

export function useCreateFolder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { name: string; parent_id?: string | null }) => unwrap(api.post<DataEnvelope<MediaFolder>>('/media-folders', input)),
    onSuccess: (folder) => {
      qc.setQueryData<MediaFolder[]>(mediaKeys.folders(), (prev) => (prev && !prev.some((f) => f.id === folder.id) ? [...prev, folder] : prev))
      void qc.invalidateQueries({ queryKey: mediaKeys.folders() })
    },
  })
}

/** Rename and/or move — applied optimistically to the folder list. */
export function useUpdateFolder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string; name?: string; parent_id?: string | null }) => unwrap(api.patch<DataEnvelope<MediaFolder>>(`/media-folders/${id}`, input)),
    onMutate: async ({ id, ...input }) => {
      await qc.cancelQueries({ queryKey: mediaKeys.folders() })
      const prev = qc.getQueryData<MediaFolder[]>(mediaKeys.folders())
      if (prev) qc.setQueryData<MediaFolder[]>(mediaKeys.folders(), prev.map((f) => (f.id === id ? { ...f, ...input } : f)))
      return { prev }
    },
    onError: (_e, _v, ctx) => ctx?.prev && qc.setQueryData(mediaKeys.folders(), ctx.prev),
    onSettled: (_d, _e, vars) => {
      void qc.invalidateQueries({ queryKey: mediaKeys.folders() })
      // Media embed `folder.name`.
      if (vars.name !== undefined) void qc.invalidateQueries({ queryKey: mediaKeys.lists() })
    },
  })
}

export function useDeleteFolder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/media-folders/${id}`),
    onSuccess: (_d, id) => {
      qc.setQueryData<MediaFolder[]>(mediaKeys.folders(), (prev) => prev?.filter((f) => f.id !== id))
      void qc.invalidateQueries({ queryKey: mediaKeys.folders() })
    },
  })
}
